import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { technicalAPI, oltAPI, acsAPI, customerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Search, Wifi, WifiOff, Radio, Cpu, GitBranch, X, Power, PowerOff, RotateCcw, Trash2, Phone, UserX } from 'lucide-react';
import { LinkStateFilterBar, LINK_STATE_META, DOWN_CAUSE_META } from '../../components/technical/LinkHealthPanel';
import { EmptyState } from '../../components/technical/TechnicalUI';
import { useAuth } from '../../context/AuthContext';
import useDarkReady from '../../theme/useDarkReady';

// Same overlay/card classes the rest of the app's modals use (Adjust Amount, Advance Payment
// Link, …) — already safe-area aware for the PWA's notch/status-bar, so this doesn't need its
// own responsive handling. Field rows get explicit word-break: this is the popup a technician
// opens to read out a customer's plan/address over the phone, so a long plan name or address
// wrapping instead of overflowing off a 375px screen matters here more than almost anywhere else.
function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function DetailField({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--cb-f1f5f9)' }}>
      <span style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '.85rem', fontWeight: 600, textAlign: 'right', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  );
}

// Same data the card already renders (technicalAPI.getConnections's row shape), just laid out
// as a full readable sheet instead of the card's compact facts row — no extra API call, since
// everything here is already sitting in `r`.
function CustomerDetailPopup({ r, onClose }) {
  return (
    <Modal title={r.name} onClose={onClose}>
      <DetailField label="Area" value={r.area} />
      <DetailField label="Address" value={r.line1} />
      {r.mobile && (
        <DetailField label="Mobile" value={<a href={`tel:${r.mobile}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>{r.mobile}</a>} />
      )}
      <DetailField label="PPPoE Username" value={r.username} />
      <DetailField label="Plan" value={r.plan ? `${r.plan.name} (${r.plan.download}/${r.plan.upload} ${r.plan.unit || 'Mbps'})` : 'Not assigned'} />
      <DetailField label="Session" value={r.sessionOnline == null ? '—' : r.sessionOnline ? 'Active' : 'None'} />
      <DetailField label="Fiber" value={r.fiberOnline == null ? '—' : r.fiberOnline ? 'Up' : 'Down'} />
      <DetailField label="Router Model" value={r.routerModel} />
      <DetailField label="Static IP" value={r.staticIp} />
      <DetailField label="Expiry" value={r.expiryDate ? new Date(r.expiryDate).toLocaleDateString() : null} />
    </Modal>
  );
}

// Same reading of the OLT's raw admin-state string as OLTPage's toggle — anything other than
// literally 'disabled' counts as enabled, since the OLT reports it as free text ('Working', etc).
const onuEnabled = (adminState) => (adminState || '').toLowerCase() !== 'disabled';

// Same dBm thresholds/colors as OLTPage's SignalBadge, kept in sync deliberately.
function SignalBadge({ dbm }) {
  if (dbm == null || dbm === '') return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const val = parseFloat(dbm);
  let color = 'var(--c-065f46)', bg = 'var(--cb-d1fae5)';
  if (val < -27) { color = 'var(--c-991b1b)'; bg = 'var(--cb-fee2e2)'; }
  else if (val < -24) { color = 'var(--c-92400e)'; bg = 'var(--cb-fef3c7)'; }
  return <span className="badge" style={{ background: bg, color }}>{val} dBm</span>;
}

// The old OnlineMeter (a single online/offline ratio bar) lived here — LinkHealthPanel
// replaces it: same "% up" headline, but split by which layer is actually failing, and its
// chips double as the state filter the meter's online/offline chips used to be.

// Layer cell — one per OSI-ish layer, so a row shows WHERE the connection stands rather
// than a single merged online/offline. null = that layer has nothing to say (no ONU mapped,
// or no NAS poller running), which is itself worth showing as "—" rather than as "offline".
function LayerCell({ up, upLabel, downLabel }) {
  if (up === null || up === undefined) return <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>—</span>;
  return up
    ? <span className="badge badge-success"><Wifi size={10} style={{ display: 'inline', marginRight: 3 }} />{upLabel}</span>
    : <span className="badge badge-warning"><WifiOff size={10} style={{ display: 'inline', marginRight: 3 }} />{downLabel}</span>;
}

function LinkStateBadge({ state, cause }) {
  const meta = LINK_STATE_META[state];
  if (!meta) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const Icon = meta.icon;
  // On a down line the reason IS the instruction — power cut means wait, fiber fault means
  // go — so it rides right next to the state rather than hiding in the detail page.
  const why = state === 'fiberDown' && cause ? DOWN_CAUSE_META[cause] : null;
  const WhyIcon = why?.icon;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      <span className="badge" title={meta.hint} style={{ background: `${meta.color}1a`, color: meta.color, whiteSpace: 'nowrap' }}>
        <Icon size={10} style={{ display: 'inline', marginRight: 3, verticalAlign: '-1px' }} />{meta.label}
      </span>
      {why && (
        <span className="badge" title={why.hint} style={{ background: `${why.color}1a`, color: why.color, whiteSpace: 'nowrap' }}>
          <WhyIcon size={10} style={{ display: 'inline', marginRight: 3, verticalAlign: '-1px' }} />{why.label}
        </span>
      )}
    </span>
  );
}

// ─── LIVE BANDWIDTH FLOW ──────────────────────────────────────────────────────
// Per-customer throughput off the NAS's 1 s PPPoE poller. /connections seeds it, then
// /live-rates refreshes just the numbers on a timer — indexed by sessionKey (the NAS-side
// username the customer's stored username resolved to), never by the customer's username
// directly, because the two differ whenever a domain suffix is involved.
const LIVE_POLL_MS = 3000;

function liveFor(r, rates) {
  if (!r.live) return null;                       // no live session → nothing is flowing
  const v = r.sessionKey ? rates[r.sessionKey] : null;
  return v ? { rx: v[0], tx: v[1] } : { rx: r.live.rx, tx: r.live.tx };
}

function fmtRate(v) {
  if (v == null) return '0';
  if (v >= 100) return v.toFixed(0);
  if (v >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

// Plan speeds are stored in Mbps (BroadbandPlan.speed.unit defaults to 'Mbps'), but the
// unit is a free string — normalise so a Gbps plan doesn't render as a 1000x over-limit bar.
function planMbps(plan) {
  if (!plan?.download) return null;
  return /gbps/i.test(plan.unit || '') ? plan.download * 1000 : plan.download;
}

// Download against the plan's ceiling. The bar is what makes "flow" readable at a glance —
// 12 Mbps means nothing on its own, "12 of 100" means the line is idle, and a bar pinned to
// the right means the customer is saturating what they pay for (not a fault).
function FlowMeter({ live, plan }) {
  const cap = planMbps(plan);
  const pct = cap ? Math.min(100, (live.rx / cap) * 100) : 0;
  const idle = live.rx < 0.05 && live.tx < 0.05;

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--cb-f1f2f4)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', color: 'var(--c-6b7280)' }}>
          <span className="live-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: idle ? 'var(--cb-9ca3af)' : 'var(--cb-0ca30c)' }} />
          LIVE
        </span>
        <span style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--c-1e40af)' }}>
          ↓{fmtRate(live.rx)}
        </span>
        <span style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--c-ea580c)' }}>
          ↑{fmtRate(live.tx)}
        </span>
        <span style={{ fontSize: '.7rem', color: 'var(--c-9ca3af)' }}>Mbps</span>
        {cap && (
          <span style={{ marginLeft: 'auto', fontSize: '.72rem', color: 'var(--c-9ca3af)' }}>
            of {cap} plan
          </span>
        )}
      </div>
      {cap && (
        <div style={{ height: 6, borderRadius: 3, background: 'var(--cb-eef0f3)', marginTop: 7, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'var(--cb-1e40af)', borderRadius: 3, transition: 'width .6s ease-out' }} />
        </div>
      )}
    </div>
  );
}

// Compact form for the desktop table's Live column — same numbers, no bar.
function FlowCell({ live }) {
  if (!live) return <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>—</span>;
  return (
    <div style={{ fontSize: '.78rem', whiteSpace: 'nowrap', lineHeight: 1.5 }}>
      <div style={{ color: 'var(--c-1e40af)', fontWeight: 700 }}>↓{fmtRate(live.rx)}</div>
      <div style={{ color: 'var(--c-ea580c)', fontWeight: 700 }}>↑{fmtRate(live.tx)}</div>
    </div>
  );
}

// Phone row. The 10-column table is fine on a desktop and unusable on a 430px screen, so
// narrow viewports get this instead (see .narrow-only/.wide-only in index.css). Only what a
// technician standing outside a customer's house needs: who, where, what's wrong, and the
// two things they'd do next — both real 44px targets, tel: so the dialer opens directly.
function ConnectionCard({ r, live, canToggleOnu, canRebootOnu, canDeleteOnu, canDeleteCustomer, acting, onToggleOnu, onRebootOnu, onDeleteOnu, onDeleteCustomer }) {
  const navigate = useNavigate();
  const meta = LINK_STATE_META[r.linkState] || {};
  const Icon = meta.icon;
  // Down lines carry the OLT's reason under the status pill: it decides whether this row is
  // a van job or a "wait for the power to come back", which is the whole triage on a phone.
  const why = r.linkState === 'fiberDown' && r.downCause ? DOWN_CAUSE_META[r.downCause] : null;
  const WhyIcon = why?.icon;
  const [showDetail, setShowDetail] = useState(false);
  const [tr069Checking, setTr069Checking] = useState(false);
  const [tr069NoConfig, setTr069NoConfig] = useState(false);

  // The ACS only has a device row once the ONT has actually Informed it at least once — an
  // ONU can be live on the OLT with a serial but never have spoken to the ACS, which is what
  // used to send this straight into a page stuck on "Loading…" behind a "device not found"
  // toast. Checking first keeps a bad case on this list instead of a broken navigation.
  // The OLT's GPON serial also isn't always what the router reports via its own TR-069
  // Inform (seen on some ZTE units, which register under a different SerialNumber) — if the
  // direct lookup 404s, fall back to resolving the ACS device through the linked customer,
  // same as CustomerDetailPage's TR069 tab already does.
  const openTr069 = async () => {
    if (!r.olt?.serial || tr069Checking) return;
    setTr069Checking(true);
    try {
      await acsAPI.getDevice(r.olt.serial);
      navigate(`/technical/acs?serial=${encodeURIComponent(r.olt.serial)}`);
    } catch {
      try {
        const { data } = await acsAPI.getDeviceByCustomer(r._id);
        navigate(`/technical/acs?serial=${encodeURIComponent(data.data.serial)}`);
      } catch {
        setTr069NoConfig(true);
      }
    } finally {
      setTr069Checking(false);
    }
  };

  return (
    <div style={{ border: '1px solid var(--cb-e5e7eb)', borderLeft: `4px solid ${meta.color || 'var(--cb-e5e7eb)'}`, borderRadius: 12, background: 'var(--cb-ffffff)', padding: '14px 14px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, cursor: 'pointer' }} onClick={() => setShowDetail(true)}>
          <div style={{ fontWeight: 700, fontSize: '.98rem', color: 'var(--c-111827)' }}>{r.name}</div>
          <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginTop: 2 }}>{r.username || '—'}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          {Icon && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
              fontSize: '.72rem', fontWeight: 700, padding: '4px 9px', borderRadius: 20,
              background: `${meta.color}1a`, color: meta.color,
            }}>
              <Icon size={12} />{meta.label}
            </span>
          )}
          {why && (
            <span title={why.hint} style={{
              display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
              fontSize: '.7rem', fontWeight: 700, padding: '3px 8px', borderRadius: 20,
              background: `${why.color}1a`, color: why.color,
            }}>
              <WhyIcon size={11} />{why.label}
            </span>
          )}
        </div>
      </div>

      <div style={{ fontSize: '.8rem', color: 'var(--c-6b7280)', margin: '8px 0 8px' }}>
        {[r.area, r.line1].filter(Boolean).join(' · ') || '—'}
      </div>

      {/* Plan name in full. The FlowMeter below only shows the speed ceiling as a number, and
          a technician on a call is usually asked about the plan by name, not by its Mbps. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.76rem', marginBottom: 10 }}>
        <span style={{ color: 'var(--c-9ca3af)' }}>Plan</span>
        <span style={{ fontWeight: 600, color: 'var(--c-374151)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.plan ? r.plan.name : 'Not assigned'}
        </span>
        {r.plan?.download != null && (
          <span style={{ color: 'var(--c-9ca3af)', flexShrink: 0 }}>· {r.plan.download}/{r.plan.upload} {r.plan.unit || 'Mbps'}</span>
        )}
      </div>

      {/* Three facts, evenly spaced, no labels longer than the values they label. */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: '.78rem', paddingTop: 10, borderTop: '1px solid var(--cb-f1f2f4)' }}>
        <span style={{ color: 'var(--c-6b7280)' }}>Fiber <strong style={{ color: r.fiberOnline == null ? 'var(--c-9ca3af)' : r.fiberOnline ? 'var(--c-0ca30c)' : 'var(--c-d03b3b)' }}>
          {r.fiberOnline == null ? '—' : r.fiberOnline ? 'Up' : 'Down'}
        </strong></span>
        <span style={{ color: 'var(--c-6b7280)' }}>Session <strong style={{ color: r.sessionOnline == null ? 'var(--c-9ca3af)' : r.sessionOnline ? 'var(--c-0ca30c)' : 'var(--c-d03b3b)' }}>
          {r.sessionOnline == null ? '—' : r.sessionOnline ? 'Active' : 'None'}
        </strong></span>
        {r.rxPower != null && <span style={{ color: 'var(--c-6b7280)' }}>Signal <strong style={{ color: signalColor(r.rxPower) }}>{r.rxPower} dBm</strong></span>}
        {/* Tapping the PON pivots the list to everyone else on that port — the fastest way to
            tell "this one customer is down" from "this whole port is down". */}
        {r.olt && (
          <Link to={`/technical/connections?pon=${r.olt.slot}/${r.olt.port}`} style={{ color: 'var(--c-6b7280)', textDecoration: 'none' }}>
            PON <strong style={{ color: 'var(--c-1e40af)' }}>{r.olt.slot}/{r.olt.port}·{r.olt.onuId}</strong>
          </Link>
        )}
      </div>

      {live && <FlowMeter live={live} plan={r.plan} />}
      {live && (r.live?.ip || r.live?.uptime) && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: '.72rem', color: 'var(--c-9ca3af)', marginTop: 8 }}>
          {r.live.ip && <span>{r.live.ip}</span>}
          {r.live.uptime && <span>up {r.live.uptime}</span>}
        </div>
      )}

      {/* All three go straight to the exact record — no landing page, no search box. ONU and
          Fiber Map key off the PON co-ordinates we already have; TR-069 keys off the ONU
          serial, which is what the ACS is indexed by. Greyed out rather than hidden when a
          customer has no ONU mapped, so the row height stays predictable when scrolling. */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        {r.olt ? (
          <Link to={`/technical/onus/${r.olt.slot}/${r.olt.port}/${r.olt.onuId}`} style={cardBtn}>
            <Radio size={15} /> ONU
          </Link>
        ) : (
          <span style={{ ...cardBtn, opacity: .4 }}><Radio size={15} /> ONU</span>
        )}
        {r.olt?.serial ? (
          <button type="button" onClick={openTr069} disabled={tr069Checking} style={{ ...cardBtn, cursor: tr069Checking ? 'default' : 'pointer', opacity: tr069Checking ? .6 : 1 }}>
            <Cpu size={15} /> {tr069Checking ? 'Checking…' : 'TR-069'}
          </button>
        ) : (
          <span style={{ ...cardBtn, opacity: .4 }}><Cpu size={15} /> TR-069</span>
        )}
        <Link to="/technical/fiber-map" style={cardBtn}>
          <GitBranch size={15} /> Fiber
        </Link>
      </div>

      {/* Separate row, not more tiles: these change hardware state instead of navigating, so
          they need their own visual weight (color-coded, full width) rather than blending into
          the three lookup buttons above. Each button only appears once the technician's Site
          Role grants the matching olt.* right, and only when the row actually has an ONU to
          act on. */}
      <OnuActionsRow
        r={r} busy={!!acting[r._id]}
        canToggle={canToggleOnu} canReboot={canRebootOnu} canDelete={canDeleteOnu}
        onToggle={onToggleOnu} onReboot={onRebootOnu} onDelete={onDeleteOnu}
        full
      />
      {/* 'unknown' rows have no ONU and no session — OnuActionsRow above renders nothing for
          them, since there's no hardware to act on. These are the dead/incomplete records
          (no plan, never provisioned) that make up the "Offline" bucket — the useful action
          there is removing the customer record itself, not an ONU command. */}
      {!r.olt && r.linkState === 'unknown' && canDeleteCustomer && (
        <button
          type="button"
          disabled={!!acting[r._id]}
          onClick={() => onDeleteCustomer(r)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            width: '100%', minHeight: 44, marginTop: 8, font: 'inherit', fontSize: '.85rem', fontWeight: 600,
            cursor: acting[r._id] ? 'default' : 'pointer', opacity: acting[r._id] ? .6 : 1,
            borderRadius: 10, border: '1px solid var(--cb-fca5a5)', background: 'var(--cb-fee2e2)', color: 'var(--c-991b1b)',
          }}
        >
          <UserX size={15} /> Delete Customer Record
        </button>
      )}

      {showDetail && <CustomerDetailPopup r={r} onClose={() => setShowDetail(false)} />}
      {tr069NoConfig && (
        <Modal title="TR-069" onClose={() => setTr069NoConfig(false)}>
          <EmptyState icon={Radio} text="No configuration of TR069 done for this customer." />
        </Modal>
      )}
    </div>
  );
}

// Same dBm thresholds as SignalBadge — on the phone card the value is shown bare (no pill),
// so the color has to ride on the number itself. Signal is the one figure a fiber tech
// reads at a glance, so it never renders in flat ink.
function signalColor(dbm) {
  const v = parseFloat(dbm);
  if (v < -27) return 'var(--c-991b1b)';
  if (v < -24) return 'var(--c-92400e)';
  return 'var(--c-065f46)';
}

const pageBtn = { flex: 1, minHeight: 46, fontSize: '.9rem' };

const cardBtn = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  minHeight: 44, borderRadius: 10, border: '1px solid var(--cb-e5e7eb)', background: 'var(--cb-ffffff)',
  fontSize: '.85rem', fontWeight: 600, color: '#374151', textDecoration: 'none',
};

// One hardware action button — shared shell for Restart/Toggle/Delete so the three read as one
// family (same shape, same busy/disabled handling) while each carries its own color: neutral
// blue for a reversible restart, amber/green for the toggle (matches the semantics already
// established on the admin OLT page), red for the destructive delete. `full` makes it a
// full-width labeled row (phone card); otherwise it collapses to an icon-only button sized to
// sit inline in a table cell.
function OnuActionButton({ icon: IconComp, label, color, bg, border, busy, onClick, full }) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      title={label}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        font: 'inherit', cursor: busy ? 'default' : 'pointer', opacity: busy ? .6 : 1,
        borderRadius: 10, border: `1px solid ${border}`, background: bg, color,
        ...(full
          ? { flex: 1, minHeight: 44, fontSize: '.85rem', fontWeight: 600 }
          : { minHeight: 32, minWidth: 32, padding: '4px 8px', fontSize: '.72rem', fontWeight: 700 }),
      }}
    >
      <IconComp size={full ? 15 : 12} />
      {full && label}
    </button>
  );
}

// The three hardware actions available on a connection's ONU, gated independently (a Site Role
// can grant Restart without Delete, etc — same per-action rights as OLTPage). Renders nothing
// when the row has no mapped ONU or the technician holds none of the three rights, so it never
// leaves an empty gap.
function OnuActionsRow({ r, busy, canToggle, canReboot, canDelete, onToggle, onReboot, onDelete, full }) {
  if (!r.olt || (!canToggle && !canReboot && !canDelete)) return null;
  const enabled = onuEnabled(r.olt.adminState);
  return (
    <div style={{ display: 'flex', gap: full ? 8 : 4, marginTop: full ? 8 : 0 }}>
      {canReboot && (
        <OnuActionButton
          icon={RotateCcw} label="Restart" color="var(--c-1e40af)" bg="var(--cb-dbeafe)" border="var(--cb-93c5fd)"
          busy={busy} full={full} onClick={() => onReboot(r)}
        />
      )}
      {canToggle && (
        <OnuActionButton
          icon={enabled ? PowerOff : Power}
          label={enabled ? 'Deactivate' : 'Activate'}
          color={enabled ? 'var(--c-92400e)' : 'var(--c-065f46)'}
          bg={enabled ? 'var(--cb-fef3c7)' : 'var(--cb-d1fae5)'}
          border={enabled ? 'var(--cb-fcd34d)' : 'var(--cb-6ee7b7)'}
          busy={busy} full={full} onClick={() => onToggle(r)}
        />
      )}
      {canDelete && (
        <OnuActionButton
          icon={Trash2} label="Delete" color="var(--c-991b1b)" bg="var(--cb-fee2e2)" border="var(--cb-fca5a5)"
          busy={busy} full={full} onClick={() => onDelete(r)}
        />
      )}
    </div>
  );
}

export default function TechnicalConnectionsPage() {
  useDarkReady();
  const { hasModuleRight, isAdmin, isStaff, isCoAdmin } = useAuth();
  // Same three Site Role rights that already gate these actions on the admin OLT page — a
  // technical login only sees each button once someone has granted that specific olt.* right.
  const canToggleOnu = hasModuleRight('olt', 'toggleState');
  const canRebootOnu = hasModuleRight('olt', 'reboot');
  const canDeleteOnu = hasModuleRight('olt', 'deleteOnu');
  // DELETE /customers/:id is staffOrAdmin on the backend — a technical login gets a hard 403
  // no matter what its Site Role rights say, since that check runs before checkModuleRight is
  // even reached. Gating on isStaff/isCoAdmin/isAdmin here (not hasModuleRight alone) keeps
  // this button from appearing for a role that can never actually use it.
  const canDeleteCustomer = isAdmin || ((isStaff || isCoAdmin) && hasModuleRight('customers', 'delete'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [liveRates, setLiveRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  // Keyed by customer _id, true while any of that row's three hardware actions is in flight —
  // shared across Restart/Toggle/Delete since they all touch the same ONU and shouldn't race.
  const [acting, setActing] = useState({});

  // ?state=… is how the dashboard's Link Health chips drill in here, ?pon=… is how the PON
  // page and each card's PON chip do. Kept in the URL (not just component state) so those
  // links are shareable and survive a refresh. The two compose: ?pon=0/4&state=fiberDown is
  // "everyone down on port 4", which is the fiber-cut view.
  const linkState = searchParams.get('state') || '';
  const pon = searchParams.get('pon') || '';
  // ?cause=… narrows the fiberDown set to one deregister reason (power cut vs fiber fault).
  // Only meaningful alongside state=fiberDown, which is the only way the card links here.
  const cause = searchParams.get('cause') || '';
  // ?onlineOnly=true|false — same reasoning as the three above: kept in the URL (not just a
  // local <select>) so the Link Health card's "PPPoE sessions up" box can deep-link straight
  // into the filtered list instead of landing on the unfiltered one and looking broken.
  const onlineOnly = searchParams.get('onlineOnly') || '';

  const setParams = (next) => {
    setPage(1);
    const merged = {
      ...(pon ? { pon } : {}),
      ...(linkState ? { state: linkState } : {}),
      ...(cause ? { cause } : {}),
      ...(onlineOnly ? { onlineOnly } : {}),
      ...next,
    };
    for (const k of Object.keys(merged)) if (!merged[k]) delete merged[k];
    setSearchParams(merged, { replace: true });
  };
  // Changing state by hand drops the cause with it — a cause is a slice of fiberDown and
  // means nothing hanging off any other state.
  const setLinkState = (s) => setParams({ state: s, cause: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await technicalAPI.getConnections({
        page, limit: 25, search: search || undefined,
        onlineOnly: onlineOnly || undefined,
        linkState: linkState || undefined,
        pon: pon || undefined,
        downCause: cause || undefined,
      });
      setRows(data.data);
      setTotal(data.pagination.total);
      setPages(data.pagination.pages);
    } catch {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, [page, search, onlineOnly, linkState, pon, cause]);

  useEffect(() => { load(); }, [load]);

  // Bandwidth flow refreshes on its own short timer rather than by reloading the list —
  // /live-rates is an in-memory read (no Mongo, no OLT), so this stays cheap enough to run
  // continuously while the technician is looking at a customer.
  useEffect(() => {
    let mounted = true;
    const tick = () => technicalAPI.getLiveRates()
      .then(({ data }) => { if (mounted) setLiveRates(data.data || {}); })
      .catch(() => {});
    tick();
    const t = setInterval(tick, LIVE_POLL_MS);
    return () => { mounted = false; clearInterval(t); };
  }, []);
  // Refreshed alongside the list so the health breakdown never disagrees with the rows
  // underneath it (both read the same 2-min OLT cache + live NAS poller).
  const loadSummary = useCallback(() => {
    technicalAPI.getSummary(pon ? { pon } : undefined)
      .then(({ data }) => setSummary(data.data)).catch(() => {});
  }, [pon]);
  useEffect(() => { loadSummary(); }, [loadSummary, linkState, onlineOnly]);

  // Shared runner for all three hardware actions. Each one changes state on the OLT itself
  // (setONUAdminState/rebootONU/deregisterONU all invalidate the OLT's cached scrape
  // server-side), so re-running `load`/`loadSummary` right after picks up the change
  // immediately instead of waiting for the next poll — a toggle or delete moves the row
  // between Attention tiles (e.g. into/out of "Deactivated by us"), so both need to move
  // together.
  const runOnuAction = async (r, { confirmMsg, run, successMsg, failMsg }) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActing(p => ({ ...p, [r._id]: true }));
    try {
      await run();
      toast.success(successMsg);
      load();
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.message || failMsg);
    } finally {
      setActing(p => { const n = { ...p }; delete n[r._id]; return n; });
    }
  };

  const handleToggleOnu = (r) => {
    const enabled = onuEnabled(r.olt.adminState);
    const action = enabled ? 'disable' : 'enable';
    const label = enabled ? 'Deactivate' : 'Activate';
    runOnuAction(r, {
      confirmMsg: `${label} ONU for ${r.name}?`,
      run: () => oltAPI.setONUState(r.olt.slot, r.olt.port, r.olt.onuId, action),
      successMsg: `ONU ${action}d`,
      failMsg: `${label} failed`,
    });
  };

  const handleRebootOnu = (r) => {
    runOnuAction(r, {
      confirmMsg: `Restart ONU for ${r.name}? Their connection will briefly drop.`,
      run: () => oltAPI.rebootONU(r.olt.slot, r.olt.port, r.olt.onuId),
      successMsg: 'Reboot command sent',
      failMsg: 'Reboot failed',
    });
  };

  // Bulk companion to the per-row Restart button, only offered on the two Attention buckets
  // where every row's fix genuinely is "reboot the ONU": "Fixable by phone" (linkState=noSession)
  // and "Check data" (linkState=mismatch) — the mismatch case is traffic flowing but the OLT's
  // own scrape stuck on "down", and a reboot forces the ONU to re-register and refresh that
  // scrape instead of waiting on the next poll. Other buckets (fiberDown, deactivated, unknown)
  // need a technician visit or a different action, so this button only appears on these two.
  // Re-fetches with a high limit rather than looping just the current 25-row page, since "All"
  // needs to mean every match, not just what's currently on screen.
  const [restartingAll, setRestartingAll] = useState(false);
  const handleRestartAll = async () => {
    const label = LINK_STATE_META[linkState]?.label || 'these';
    if (!window.confirm(`Restart ONUs for all ${total} "${label}" connection(s)? Each customer's connection will briefly drop.`)) return;
    setRestartingAll(true);
    try {
      const { data } = await technicalAPI.getConnections({
        page: 1, limit: total || 500,
        search: search || undefined, onlineOnly: onlineOnly || undefined,
        linkState, pon: pon || undefined, downCause: cause || undefined,
      });
      const targets = (data.data || []).filter(r => r.olt);
      let ok = 0, failed = 0;
      for (const r of targets) {
        try { await oltAPI.rebootONU(r.olt.slot, r.olt.port, r.olt.onuId); ok++; }
        catch { failed++; }
      }
      toast[failed ? 'error' : 'success'](`Restarted ${ok}${failed ? `, ${failed} failed` : ''}`);
      load();
      loadSummary();
    } catch {
      toast.error('Failed to load connections for restart');
    } finally {
      setRestartingAll(false);
    }
  };

  // Bulk companion to the per-row Delete (deregister) button — offered only on "Check data"
  // (linkState=mismatch), not on "Fixable by phone": a mismatch row's ONU registration itself
  // is the thing in question (stale/wrong mapping), so wiping it and letting the OLT's next
  // scrape re-register it fresh is a legitimate bulk fix. A noSession row's ONU is fine — its
  // session is the problem — so deregistering it would only strand a customer who has nothing
  // wrong with their fiber. Same high-limit re-fetch as handleRestartAll, for the same reason.
  const [deletingAll, setDeletingAll] = useState(false);
  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete (deregister) ONUs for all ${total} "Check data" connection(s)? This cannot be undone — each ONU will need to be re-provisioned, either automatically on its next check-in or by a technician on site.`)) return;
    setDeletingAll(true);
    try {
      const { data } = await technicalAPI.getConnections({
        page: 1, limit: total || 500,
        search: search || undefined, onlineOnly: onlineOnly || undefined,
        linkState, pon: pon || undefined, downCause: cause || undefined,
      });
      const targets = (data.data || []).filter(r => r.olt);
      let ok = 0, failed = 0;
      for (const r of targets) {
        try { await oltAPI.deregisterONU(r.olt.slot, r.olt.port, r.olt.onuId); ok++; }
        catch { failed++; }
      }
      toast[failed ? 'error' : 'success'](`Deregistered ${ok}${failed ? `, ${failed} failed` : ''}`);
      load();
      loadSummary();
    } catch {
      toast.error('Failed to load connections for delete');
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteOnu = (r) => {
    runOnuAction(r, {
      confirmMsg: `Delete ONU for ${r.name}? This deregisters it from the OLT and cannot be undone — a technician will need to re-provision it on site.`,
      run: () => oltAPI.deregisterONU(r.olt.slot, r.olt.port, r.olt.onuId),
      successMsg: 'ONU deregistered',
      failMsg: 'Delete failed',
    });
  };

  // Only reachable for 'unknown' rows (no ONU, no session — see the button's render guard),
  // so this is a customer record with nothing live tied to it, not an active line.
  const handleDeleteCustomer = (r) => {
    runOnuAction(r, {
      confirmMsg: `Permanently delete ${r.name}'s customer record? This removes their entire profile, billing history and payments — not just this connection. This cannot be undone.`,
      run: () => customerAPI.delete(r._id),
      successMsg: 'Customer deleted',
      failMsg: 'Delete failed',
    });
  };

  return (
    <div>
      <div className="page-header">
        <h2>Active Connections</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
          Broadband customers, their plan, and live line status.
        </p>
      </div>

      {/* A PON filter changes what every count below means, so it gets a banner rather than
          a quiet chip — arriving here from a PON card and not noticing the scope would make
          "2 down" read as the whole network. */}
      {pon && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0',
          border: '1px solid var(--cb-bfdbfe)', background: 'var(--cb-eff6ff)', borderRadius: 12, padding: '12px 14px',
        }}>
          <GitBranch size={17} color="var(--c-1e40af)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--c-1e3a8a)' }}>PON {pon}</div>
            {/* Not "0 customers" while the count is still in flight — on a slow phone
                connection that reads as an empty port rather than as a pending request. */}
            <div style={{ fontSize: '.75rem', color: 'var(--c-3b82f6)' }}>
              {loading ? 'Loading…' : `${total} ${total === 1 ? 'customer' : 'customers'} on this port`}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setParams({ pon: '' })}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, minHeight: 40, padding: '0 12px',
              borderRadius: 10, border: '1px solid var(--cb-bfdbfe)', background: 'var(--cb-ffffff)',
              font: 'inherit', fontSize: '.8rem', color: '#1e40af', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <X size={14} /> Clear
          </button>
        </div>
      )}

      {summary && (
        <>
          <LinkStateFilterBar
            health={summary.linkHealth}
            activeState={linkState}
            activeCause={cause}
            onStateChange={setLinkState}
            onCauseChange={(c) => setParams({ cause: c })}
          />
          {/* The three stat cards that used to sit here are now this one quiet line — the
              filter pills above already carry every count that gets acted on. */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: '.78rem', color: 'var(--c-9ca3af)', margin: '0 4px 14px' }}>
            <span>{summary.broadbandActive} {pon ? 'on this port' : 'active broadband'}</span>
            <span>{summary.onlineNow} online now</span>
            <span>{summary.areaCount} {summary.areaCount === 1 ? 'area' : 'areas'}</span>
          </div>
        </>
      )}

      {((linkState === 'noSession' || linkState === 'mismatch') && canRebootOnu && total > 0) ||
      (linkState === 'mismatch' && canDeleteOnu && total > 0) ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          {(linkState === 'noSession' || linkState === 'mismatch') && canRebootOnu && (
            <button
              type="button"
              className="btn btn-primary"
              style={{ minHeight: 44, display: 'flex', alignItems: 'center', gap: 6 }}
              disabled={restartingAll || deletingAll}
              onClick={handleRestartAll}
            >
              <RotateCcw size={15} /> {restartingAll ? 'Restarting…' : `Restart All (${total})`}
            </button>
          )}
          {linkState === 'mismatch' && canDeleteOnu && (
            <button
              type="button"
              className="btn btn-danger"
              style={{ minHeight: 44, display: 'flex', alignItems: 'center', gap: 6 }}
              disabled={restartingAll || deletingAll}
              onClick={handleDeleteAll}
            >
              <Trash2 size={15} /> {deletingAll ? 'Deleting…' : `Delete All (${total})`}
            </button>
          )}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px' }}>
          <Search size={17} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            className="form-control"
            style={{ paddingLeft: 38, minHeight: 46, fontSize: '.95rem' }}
            placeholder="Search name, username, area, mobile..."
            value={search}
            onChange={e => { setPage(1); setSearch(e.target.value); }}
          />
        </div>
        {/* Desktop only: on a phone the state pills above already are the filter, and two
            competing filter controls is exactly the clutter that made this unusable in the
            field. The coarse online/offline cut stays available on the wide layout. */}
        <select
          className="form-control wide-only"
          style={{ flex: '1 1 150px', maxWidth: 200, minHeight: 46, fontSize: '.95rem' }}
          value={onlineOnly}
          onChange={e => setParams({ onlineOnly: e.target.value })}
        >
          <option value="">All Status</option>
          <option value="true">Online only</option>
          <option value="false">Offline only</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
      <>
      {/* Phone: one card per connection, with the verdict and the two actions a technician
          reaches for (call the customer, open their record) as real 44px targets. */}
      <div className="narrow-only">
        {rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: '.9rem' }}>No connections found</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map(r => (
            <ConnectionCard
              key={r._id} r={r} live={liveFor(r, liveRates)}
              canToggleOnu={canToggleOnu} canRebootOnu={canRebootOnu} canDeleteOnu={canDeleteOnu}
              canDeleteCustomer={canDeleteCustomer}
              acting={acting}
              onToggleOnu={handleToggleOnu} onRebootOnu={handleRebootOnu} onDeleteOnu={handleDeleteOnu}
              onDeleteCustomer={handleDeleteCustomer}
            />
          ))}
        </div>
      </div>

      <div className="table-container wide-only">
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Username</th>
                <th>Area</th>
                <th>Plan</th>
                <th>Fiber</th>
                <th>Session</th>
                <th>Verdict</th>
                <th>Live Mbps</th>
                <th>Power</th>
                <th>OLT</th>
                <th>Expiry</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No connections found</td></tr>
              )}
              {rows.map(r => (
                <tr key={r._id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{r.mobile}</div>
                  </td>
                  <td style={{ fontSize: '.85rem' }}>{r.username || '—'}</td>
                  <td style={{ fontSize: '.85rem' }}>{r.area}</td>
                  <td style={{ fontSize: '.85rem' }}>
                    {r.plan ? `${r.plan.name} (${r.plan.download}/${r.plan.upload} ${r.plan.unit || 'Mbps'})` : '—'}
                  </td>
                  <td><LayerCell up={r.fiberOnline} upLabel="Up" downLabel="Down" /></td>
                  <td><LayerCell up={r.sessionOnline} upLabel="Active" downLabel="None" /></td>
                  <td><LinkStateBadge state={r.linkState} cause={r.downCause} /></td>
                  <td><FlowCell live={liveFor(r, liveRates)} /></td>
                  <td><SignalBadge dbm={r.rxPower} /></td>
                  <td style={{ fontSize: '.8rem' }}>
                    {r.olt ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>PON {r.olt.slot}/{r.olt.port} · ONU {r.olt.onuId}</span>
                        <OnuActionsRow
                          r={r} busy={!!acting[r._id]}
                          canToggle={canToggleOnu} canReboot={canRebootOnu} canDelete={canDeleteOnu}
                          onToggle={handleToggleOnu} onReboot={handleRebootOnu} onDelete={handleDeleteOnu}
                        />
                      </div>
                    ) : r.linkState === 'unknown' && canDeleteCustomer ? (
                      <OnuActionButton
                        icon={UserX} label="Delete Customer" color="var(--c-991b1b)" bg="var(--cb-fee2e2)" border="var(--cb-fca5a5)"
                        busy={!!acting[r._id]} onClick={() => handleDeleteCustomer(r)}
                      />
                    ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
                    {r.expiryDate ? new Date(r.expiryDate).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>
      </>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
          <button className="btn" style={pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span style={{ fontSize: '.85rem', color: 'var(--c-6b7280)', whiteSpace: 'nowrap' }}>Page {page} of {pages}</span>
          <button className="btn" style={pageBtn} disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
